<div class="npdc-title">
  <h1>Welcome to the Norwegian Polar Data Centre</h1>
  <div class="npdc-title__actions">
    @if (authService.user(); as user) {
      <a mat-flat-button routerLink="new">New dataset</a>
    }
  </div>
</div>

<p>
  This data catalogue provides open and direct access to scientific data, environmental monitoring
  data, and topographic and geological map data from the polar regions. Please cite the originators
  when reusing data.
</p>

<p>To filter by area use the button next to the search input.</p>

<div class="npdc-search">
  <div class="npdc-search__input">
    <div class="npdc-search__input__prefix">
      <mat-icon>search</mat-icon>
    </div>
    <input
      type="search"
      placeholder="Search in datasets..."
      [value]="q() || ''"
      (keydown.enter)="onFilterChange('q', $event!.target!.value || undefined)"
    />
  </div>
  <button mat-icon-button (click)="advancedSearch()" matTooltip="Advanced search">
    <mat-icon>tune</mat-icon>
  </button>
</div>

<!-- filters -->
<mat-chip-set>
  @if (authService.user()) {
    @let r = role();
    <mat-chip-option
      [selectable]="false"
      [selected]="!!r"
      [matMenuTriggerFor]="roleMenu"
      matTooltip="Role filter"
    >
      @switch (r) {
        @case ('reader') {
          <span>Reader</span>
        }
        @case ('contributor') {
          <span>Contributor</span>
        }
        @case ('owner') {
          <span>Owner</span>
        }
        @default {
          <span>My role</span>
        }
      }

      <mat-icon matChipTrailingIcon>arrow_drop_down</mat-icon>
    </mat-chip-option>

    @let t = type();
    <mat-chip-option
      [selectable]="false"
      [selected]="!!t"
      [matMenuTriggerFor]="typeMenu"
      matTooltip="Status filter"
    >
      @if (t === 'draft') {
        <span>Draft</span>
      } @else if (t === 'public') {
        <span>Public</span>
      } @else if (t === 'archived') {
        <span>Archived</span>
      } @else {
        <span>Status</span>
      }
      <mat-icon matChipTrailingIcon>arrow_drop_down</mat-icon>
    </mat-chip-option>
  }

  @if (order(); as value) {
    <mat-chip highlighted matTooltip="Order by">
      {{ value.replace(/\:.*$/, '') | titlecase }}
      <mat-icon matChipTrailingIcon style="padding-right: 0">
        @if (value.endsWith('desc')) {
          arrow_downward
        } @else {
          arrow_upward
        }
      </mat-icon>
      <mat-icon matChipRemove (click)="onFilterChange('order', undefined)">cancel</mat-icon>
    </mat-chip>
  }

  @if (from() && until()) {
    <mat-chip highlighted matTooltip="Date range filter">
      {{ dateField() | titlecase }} between {{ dateFrom() | date }} and {{ dateUntil() | date }}
      <mat-icon matChipRemove (click)="onFilterChange(['from', 'until'], undefined)"
        >cancel</mat-icon
      >
    </mat-chip>
  } @else {
    @if (from()) {
      <mat-chip highlighted matTooltip="After date filter">
        {{ dateField() | titlecase }} after {{ dateFrom() | date }}
        <mat-icon matChipRemove (click)="onFilterChange('from', undefined)">cancel</mat-icon>
      </mat-chip>
    }

    @if (until()) {
      <mat-chip highlighted matTooltip="Before date filter">
        {{ dateField() | titlecase }} before {{ dateUntil() | date }}
        <mat-icon matChipRemove (click)="onFilterChange('until', undefined)">cancel</mat-icon>
      </mat-chip>
    }
  }

  @if (location(); as value) {
    <mat-chip highlighted matTooltip="Custom location filter">
      Location
      <mat-icon matChipRemove (click)="onFilterChange('location', undefined)">cancel</mat-icon>
    </mat-chip>
  }

  @if (labelId(); as value) {
    <mat-chip highlighted matTooltip="Project filter">
      @if (label.hasValue()) {
        {{ label.value().title }}
      } @else {
        Unknown label
      }
      <mat-icon matChipRemove (click)="onFilterChange('labelId', undefined)">cancel</mat-icon>
    </mat-chip>
  }
</mat-chip-set>

@if (mapExpanded()) {
  <p>
    <mat-card class="npdc-leaflet" appearance="outlined">
      <app-leaflet-group [geometry]="geojson()" [readonly]="true" [height]="'250px'" />
    </mat-card>
  </p>
}

<app-list [resource]="datasets">
  @if (!datasets.error()) {
    <mat-nav-list>
      @for (dataset of datasets.value()?.items; track dataset.id) {
        <a mat-list-item routerLink="{{ dataset.id }}">
          @let isDraft = dataset.type === 'draft';
          @let isArchived = dataset.type === 'archived';
          <mat-icon matListItemIcon>
            @switch(dataset.type) {
              @case('draft') { draft }
              @case('archived') { elderly }
              @default { article }
            }
          </mat-icon>
          <span matListItemTitle>{{ isDraft ? '[Draft] ' : isArchived ? '[Archived] ' : '' }}{{ dataset.content.title }}</span>
          <span matListItemLine>{{ authors(dataset) || 'N/A' }}</span>
          <span matListItemLine>
            @let doi = dataset.doi.toUpperCase();
            @if (dataset.type !== 'draft') {
              <span class="npdc-doi-link">{{ doi }}</span>
            } @else {
              <span>{{ doi }}</span>
            }
          </span>
          @if (dataset.type !== 'draft') {
            <span matListItemMeta>
              {{ dataset.published | date }}
            </span>
          }
        </a>
      }
    </mat-nav-list>

    <mat-paginator
      showFirstLastButtons
      (page)="onPageEvent($event)"
      [length]="count()"
      [pageIndex]="page()"
      [pageSize]="pageSize()"
      [pageSizeOptions]="[10, 20, 50, 100]"
    >
    </mat-paginator>
  }
</app-list>

<ng-template #optionTemplate let-value="value">
  @switch (value) {
    @case ('antarctica') {
      Antarctica
    }
    @case ('arcticOcean') {
      Arctic Ocean
    }
    @case ('svalbard') {
      Svalbard
    }
    @case ('draft') {
      Draft
    }
    @case ('public') {
      Public
    }
    @default {
      {{ value }}
    }
  }
</ng-template>

<mat-menu #typeMenu="matMenu">
  <button mat-menu-item (click)="onFilterChange('type', undefined)">All</button>
  <button mat-menu-item (click)="onFilterChange('type', 'public')">Public</button>
  <button mat-menu-item (click)="onFilterChange('type', 'draft')">Draft</button>
  <button mat-menu-item (click)="onFilterChange('type', 'archived')">Archived</button>
</mat-menu>

<mat-menu #roleMenu="matMenu">
  <button mat-menu-item (click)="onFilterChange('role', undefined)">Any</button>
  <button mat-menu-item (click)="onFilterChange('role', 'reader')">Reader</button>
  <button mat-menu-item (click)="onFilterChange('role', 'contributor')">Contributor</button>
  <button mat-menu-item (click)="onFilterChange('role', 'owner')">Owner</button>
</mat-menu>
